import { Preferences } from "@capacitor/preferences";
import { motion } from "framer-motion";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { VerificationCodeForm } from "@/modules/onboarding/components/VerificationCodeInput/VerificationCodeForm";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { storage } from "@/modules/storage";
import { retrieveReferralLinkClaimEligibility } from "@/api/orval/referral-codes/referral-codes";
import { getUser, postAuthLogin, postAuthVerify, postMultiPlatformAuthVerify } from "api/methods";
import { queryClient } from "pages/Root";
import Services from "services/Services";
import { isNativeMobileApp } from "utils/platform.helpers";

import { getPostWebAuthVerifyPath, profileDataFromUser } from "./postWebAuthVerifyNavigation";
import { V3ReferralOfferForNewClientsPage_PATH } from "./V3ReferralOfferForNewClientsPage";

// Match legacy URL composition used in VerificationCodePage
const getEnvScheme = () =>
  new URL(import.meta.env.REACT_APP_API_BASE_URL ?? location.href).protocol;

export const getMultiPlatformSelectorPath = () => "/platform-selector?fromSignIn=true";

const V3VerificationCodePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    flow,
    referral,
    platformSelection,
    setPlatformId,
    auth,
    updatePlatformSelection,
    updateProfileState,
    loadExistingUserData,
  } = useOnboardingContext();

  const handleNativePlatformVerification = useCallback(
    async (verificationCode: string) => {
      const verify = await postMultiPlatformAuthVerify({
        phoneNumber: "+" + auth.phoneNumber?.replaceAll(/\D/g, ""),
        verificationCode: verificationCode,
      });

      const platformTokens = verify.platformDomainTokens;

      await Preferences.set({
        key: "multiPlatformVerifyToken",
        value: verify.token || "",
      });
      await Preferences.set({
        key: "user",
        value: JSON.stringify(verify.user),
      });
      await Preferences.set({
        key: "platformDomainTokens",
        value: JSON.stringify(platformTokens),
      });

      storage.setItem(
        "iconNames",
        JSON.stringify(platformTokens.map((p) => `launcher_${p.platformSubdomain}`)),
      );

      // Handle new user or no platforms case
      if (flow.isNewUser || platformTokens.length === 0) {
        // If the user already has a completed profile from another platform,
        // pre-populate the onboarding context so they don't re-enter profile data.
        const existingProfile = profileDataFromUser(verify.user);
        if (existingProfile) {
          updateProfileState(existingProfile);
        }

        if (platformSelection.platformId) {
          updatePlatformSelection({ canReselectPlatform: true });
          navigate(existingProfile ? "/signin/location" : "/signin/name");
          return;
        }
        navigate("/signin/platform-search");
        return;
      }

      // Handle explicit platform selection
      if (platformSelection.platformId) {
        const platformDomainToken = platformTokens.find(
          (p) => p.platformId === platformSelection.platformId,
        );

        if (!platformDomainToken) {
          navigate("/signin/platform-search");
          return;
        }

        storage.setItem("baseApiUrl", `${getEnvScheme()}//${platformDomainToken.platformDomain}`);
        await Services.auth.setToken(platformDomainToken.token);
        navigate("/home?fromSignIn=true");
        return;
      }

      // Handle single platform case
      if (platformTokens.length === 1) {
        setPlatformId(platformTokens[0].platformId);
        storage.setItem("baseApiUrl", `${getEnvScheme()}//${platformTokens[0].platformDomain}`);
        await Services.auth.setToken(platformTokens[0].token);
        navigate("/home?fromSignIn=true");
        return;
      }

      // Multiple platforms - show picker
      navigate(getMultiPlatformSelectorPath());
    },
    [
      auth.phoneNumber,
      flow.isNewUser,
      navigate,
      platformSelection.platformId,
      setPlatformId,
      updatePlatformSelection,
      updateProfileState,
    ],
  );

  const handleWebPlatformVerification = useCallback(
    async (verificationCode: string) => {
      const resp = await postAuthVerify({
        phoneNumber: auth.phoneNumber,
        verificationCode,
        statelessReferralCode:
          referral.statelessReferralCode ?? storage.getItem("statelessReferralCode") ?? undefined,
        signupReferralCode: referral.referralCode,
      });

      if (!resp || ("token" in resp && !resp.token)) {
        throw new Error(t("v3VerificationCodePage.unableToVerifyCode"));
      }

      if ("token" in resp && resp.token) {
        await Services.auth.setToken(resp.token);
      }

      await queryClient.invalidateQueries({ queryKey: getUser.key() });

      // loadExistingUserData fetches /user, populates auth/profile/platform
      // context, and returns the user object for routing decisions.
      const user = await loadExistingUserData();

      // A returning client who came in through a friend's link is only referred if they joined recently.
      if (user?.completedProfile && storage.getItem("statelessReferralCode")) {
        const { canClaimReferralLink } = await retrieveReferralLinkClaimEligibility();
        if (!canClaimReferralLink) {
          storage.removeItem("statelessReferralCode");
          storage.removeItem("referralSenderName");
          navigate(V3ReferralOfferForNewClientsPage_PATH);
          return;
        }
      }

      navigate(getPostWebAuthVerifyPath(user));
    },
    [
      auth.phoneNumber,
      navigate,
      referral.referralCode,
      referral.statelessReferralCode,
      loadExistingUserData,
      t,
    ],
  );

  const onVerificationCodeSubmitted = useCallback(
    async (verificationCode: string) => {
      // Add delay in development to see the loader
      if (import.meta.env.DEV) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      try {
        if (isNativeMobileApp()) {
          await handleNativePlatformVerification(verificationCode);
        } else {
          await handleWebPlatformVerification(verificationCode);
        }
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(t("v3VerificationCodePage.unableToVerifyCode"));
      }
    },
    [handleNativePlatformVerification, handleWebPlatformVerification, t],
  );

  const onResendConfirmationCode = useCallback(async () => {
    try {
      if (!auth.phoneNumber) {
        throw new Error(t("v3VerificationCodePage.missingPhoneNumber"));
      }
      await postAuthLogin({ phoneNumber: auth.phoneNumber });
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error(t("v3VerificationCodePage.unableToResendCode"));
    }
  }, [auth.phoneNumber, t]);

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
      >
        <VerificationCodeForm
          phoneNumber={auth.phoneNumber}
          onConfirm={onVerificationCodeSubmitted}
          onResendCode={onResendConfirmationCode}
          resendCooldownSeconds={30}
        />
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3VerificationCodePage;
